import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import userAdminService from '../../services/userAdminService';
import roleAdminService from '../../services/roleAdminService';
import groupService from '../../services/groupService';
import RoleService from '../../services/RoleService';
import useCan from '../../hooks/useCan';
import { useOptionalPermissions } from '../../context/permissionContextCore';
import iniziali from '../common/iniziali';
import { chiaveConcessione } from './concessioni';
import ConcessioniPerRuolo from './ConcessioniPerRuolo';
import FormConcessione from './FormConcessione';

// Una riga sola. La versione lunga spiegava anche come si compongono gli ambiti fra piu'
// concessioni: e' vero, ma e' una regola del motore dei permessi, non un'istruzione per
// usare questa finestra, e chiedeva di tenere a mente tre cose prima di poter leggere la
// prima riga. Chi vuole sapere cosa una persona puo' fare davvero ha «Vedi permessi
// effettivi», che lo mostra invece di spiegarlo.
const SPIEGAZIONE_CONCESSIONI =
    'Il ruolo dice che cosa la persona può fare, il gruppo su quali dati.';

/**
 * Pannello di assegnazione ruoli per un utente (spec §10.4, §10.6, §15.1).
 *
 * Una cosa va detta qui, non altrove:
 * - la revoca di un ruolo diretto impiega fino a 60 secondi per propagarsi a tutte le
 *   repliche (§15.1): il testo sta a ridosso di DialogActions, subito sopra ai pulsanti
 *   Annulla/Salva — non genericamente "nel pannello", e non in un tooltip, che lo mostra
 *   solo a chi ci passa sopra.
 *
 * Fase F6: la lista di caselle «Ruoli assegnati» e il pulsante Salva sono spariti. Erano
 * l'altra leva, quella che scriveva `user_role` — una tabella che il resolver non legge piu'
 * dalla fase F3 e che F6 ha eliminato: spuntare una casella li' dava a chi amministra la
 * sensazione di aver concesso qualcosa, e non concedeva nulla. Resta una sola leva, le
 * concessioni, che è quella che ha effetto. I ruoli della persona si vedono ancora, in sola
 * lettura, perché sapere che ruoli ha resta utile anche quando non è da qui che si cambiano.
 *
 * Le code di questa persona (`user.queues`) sono in sola lettura qui: sono quelle dei gruppi in
 * cui un suo ruolo concede `queues.read`, cioè le code che vede davvero, e non esiste un
 * endpoint che le cambi per la singola persona. Il pannello
 * lo dice in pagina, perche' chi cerca «aggiungi una coda a questa persona» deve trovare una
 * spiegazione, non un'assenza.
 *
 * Il gating è su permessi e non sulla sola apertura della pagina: la pagina è protetta da
 * `users.read`, ma questo pannello legge il catalogo dei ruoli (`roles.read`) e scrive
 * (`users.update`). Chi ha solo `users.read` deve vedere quale delle due cose è mancata,
 * non un errore che dice il falso.
 */
export default function UserRolesPanel({ sub, onClose, onSaved }) {
    const can = useCan();
    const refreshPermissions = useOptionalPermissions()?.refresh;
    const [user, setUser] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [effectivePermissions, setEffectivePermissions] = useState(null);
    const [loadingEffective, setLoadingEffective] = useState(false);
    const [catalogError, setCatalogError] = useState(null);
    // Denominatore del conteggio «17 / 45»: stessa strada di `RolesTab`, una sola richiesta
    // per l'intero pannello e non una per riga. Resta null se non arriva: in quel caso il
    // conteggio si omette invece di mostrare una frazione a meta'.
    const [totalPermissions, setTotalPermissions] = useState(null);
    // Le concessioni (ruolo + gruppo) e il catalogo dei gruppi su cui concederle. Dalla
    // fase F6 sono l'unica assegnazione che esista: `user_role` non c'è più.
    const [grants, setGrants] = useState([]);
    const [groups, setGroups] = useState([]);
    const [grantsError, setGrantsError] = useState(null);
    // Le modifiche preparate e non ancora scritte: {tipo: 'aggiunta' | 'rimozione', ...}.
    // Prima ogni clic partiva verso il backend da solo, e chi doveva spostare una persona
    // da un gruppo a un altro passava per un istante in cui non era in nessuno dei due —
    // con l'invariante dell'ultimo amministratore, quell'istante e' un rifiuto. Qui le
    // modifiche si accumulano e partono insieme, al salvataggio.
    const [pendenti, setPendenti] = useState([]);
    const [salvataggio, setSalvataggio] = useState(false);

    // §10.4 protegge la pagina con users.read; scrivere richiede users.update. Lasciare
    // «Salva» abilitato a chi non ce l'ha produce un 403 al click, dopo che le caselle si
    // sono già spuntate: il rifiuto va detto prima, e va detto perché.
    const canUpdate = can('users.update');

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        setCatalogError(null);
        // allSettled e non all: i due caricamenti chiedono permessi diversi (users.read e
        // roles.read) e fallire insieme li rende indistinguibili. Con `all`, un 403 sulla
        // sola listRoles mostrava «Non è stato possibile caricare i dati dell'utente», che
        // è falso: i dati dell'utente erano arrivati.
        Promise.allSettled([
            userAdminService.getUser(sub),
            userAdminService.listRoles(),
        ]).then(
            ([userResult, rolesResult]) => {
                if (userResult.status === 'rejected') {
                    setError('Non è stato possibile caricare i dati dell\'utente.');
                    setLoading(false);
                    return;
                }
                const userData = userResult.value;
                setUser(userData);
                if (rolesResult.status === 'rejected') {
                    setCatalog([]);
                    setCatalogError(
                        // RoleService e non `can`: siamo dentro `load`, e con `can` fra le
                        // dipendenze il pannello si ricaricherebbe a ogni refresh() dei permessi.
                        RoleService.can('roles.read')
                            ? 'Non è stato possibile caricare il catalogo dei ruoli: sono elencati i soli ruoli già assegnati.'
                            : 'Il catalogo dei ruoli richiede il permesso roles.read, che manca: sono elencati i soli ruoli già assegnati.',
                    );
                } else {
                    setCatalog(rolesResult.value || []);
                }
                setLoading(false);
            },
        );
    }, [sub]);

    // Le concessioni e il catalogo dei gruppi: due richieste separate da quelle di `load`,
    // perché chiedono permessi diversi (users.read e groups.read) e fallire insieme le
    // renderebbe indistinguibili — la stessa ragione dell'allSettled di `load`.
    const loadGrants = useCallback(() => {
        if (!sub) {
            return;
        }
        setGrantsError(null);
        Promise.allSettled([userAdminService.listGrants(sub), groupService.lista()]).then(
            ([grantsResult, groupsResult]) => {
                if (grantsResult.status === 'rejected') {
                    setGrants([]);
                    setGrantsError('Non è stato possibile caricare le concessioni.');
                } else {
                    setGrants(grantsResult.value || []);
                }
                if (groupsResult.status === 'rejected') {
                    // Senza catalogo si possono comunque leggere e revocare le concessioni
                    // esistenti: si perde solo la facoltà di aggiungerne su un gruppo.
                    setGroups([]);
                } else {
                    setGroups(groupsResult.value || []);
                }
            },
        );
    }, [sub]);

    useEffect(() => {
        if (sub) {
            load();
            loadGrants();
            setEffectivePermissions(null);
            }
    }, [sub, load, loadGrants]);

    // Il catalogo dei permessi è dato statico — la tassonomia non cambia a runtime — e questo
    // pannello lo carica una sola volta al montaggio, con dipendenze vuote. Il risparmio vale
    // finché il pannello resta montato: `RolesAndPermissions` lo rende come
    // `{selected && <UserRolesPanel .../>}`, quindi chiuderlo lo smonta, e riaprirlo su un
    // altro utente rimonta il componente e ripete la richiesta. Il vantaggio si vede solo
    // passando da una riga selezionata a un'altra senza chiudere in mezzo.
    useEffect(() => {
        roleAdminService.listPermissionCatalog().then(
            (catalog) => {
                setTotalPermissions((catalog || []).length);
            },
            () => {
                // Il conteggio dei permessi per ruolo e' un di piu' rispetto ai ruoli stessi:
                // se il catalogo non arriva (permesso mancante o errore), il pannello resta
                // comunque utilizzabile e mostra solo il ruolo senza la frazione.
                setTotalPermissions(null);
            },
        );
    }, []);

    // I ruoli che la persona ha, con la frazione dei permessi presa dal catalogo quando c'e'.
    // Un ruolo sparito dal catalogo resta in elenco senza frazione: nasconderlo lo farebbe
    // sparire dalla vista senza dirlo.
    // Quanti permessi porta ciascun ruolo: l'unico dato che l'elenco «Ruoli assegnati»
    // aveva in piu' delle concessioni, e che percio' si e' spostato sulla loro riga invece
    // di sparire con la sezione.
    const permessiPerRuolo = useMemo(
        () => new Map(catalog.map((r) => [r.id, r.permissionCount])),
        [catalog],
    );

    // Cio' che l'elenco mostra: le concessioni gia' scritte, marcate «da rimuovere» se lo
    // sono, piu' quelle ancora da scrivere. Le seconde stanno nello stesso elenco e non in
    // uno accanto: sono la stessa cosa in due momenti diversi, e separarle costringerebbe a
    // leggere due elenchi per sapere come sara' questa persona dopo il salvataggio.
    const righeConcessioni = useMemo(() => {
        const inRimozione = new Set(
            pendenti
                .filter((m) => m.tipo === 'rimozione')
                .map((m) => chiaveConcessione(m.roleId, m.groupId)),
        );
        return [
            ...grants.map((g) => ({
                ...g,
                stato: inRimozione.has(chiaveConcessione(g.roleId, g.groupId))
                    ? 'da-rimuovere'
                    : 'salvata',
            })),
            ...pendenti
                .filter((m) => m.tipo === 'aggiunta')
                .map((m) => ({ ...m, stato: 'da-aggiungere' })),
        ];
    }, [grants, pendenti]);

    // Le concessioni gia' presenti o preparate, per chiave: servono a spegnere nell'elenco i
    // gruppi in cui il ruolo scelto c'e' gia'. Detto li' e non dal 409 del backend al
    // salvataggio: il rifiuto arriverebbe dopo aver preparato altre modifiche, e non si
    // capirebbe quale lo ha provocato.
    const chiaviAttive = useMemo(
        () => new Set(righeConcessioni
            .filter((r) => r.stato !== 'da-rimuovere')
            .map((r) => chiaveConcessione(r.roleId, r.groupId))),
        [righeConcessioni],
    );

    // Le righe raggruppate per ruolo: con molti gruppi un elenco piatto ripete lo stesso
    // ruolo decine di volte, e non si legge piu' «che cosa fa questa persona». Dentro il
    // ruolo, «ogni gruppo» in testa e poi i gruppi in ordine alfabetico.
    const concessioniPerRuolo = useMemo(() => {
        const perRuolo = new Map();
        righeConcessioni.forEach((riga) => {
            if (!perRuolo.has(riga.roleId)) {
                perRuolo.set(riga.roleId, { roleId: riga.roleId, roleLabel: riga.roleLabel, righe: [] });
            }
            perRuolo.get(riga.roleId).righe.push(riga);
        });
        const ordine = (riga) => (riga.groupId ? `1${riga.groupLabel || ''}` : '0');
        return [...perRuolo.values()].map((voce) => ({
            ...voce,
            righe: [...voce.righe].sort((a, b) => ordine(a).localeCompare(ordine(b))),
        }));
    }, [righeConcessioni]);

    // Le concessioni preparate da «Concedi un ruolo» si accumulano con le altre modifiche,
    // e partono tutte insieme al salvataggio.
    const handlePrepara = (nuove) => {
        setPendenti((prev) => [...prev, ...nuove]);
    };

    const handleRevoke = (riga) => {
        setGrantsError(null);
        const chiave = chiaveConcessione(riga.roleId, riga.groupId);
        if (riga.stato === 'da-aggiungere') {
            // Non era ancora stata scritta: si toglie dalle modifiche preparate, non si
            // prepara una rimozione di qualcosa che non esiste.
            setPendenti((prev) => prev.filter(
                (m) => !(m.tipo === 'aggiunta'
                    && chiaveConcessione(m.roleId, m.groupId) === chiave)));
            return;
        }
        setPendenti((prev) => [...prev, { tipo: 'rimozione', ...riga }]);
    };

    /** Rimette una concessione marcata «da rimuovere»: l'errore di un clic si annulla. */
    const handleRipristina = (riga) => {
        const chiave = chiaveConcessione(riga.roleId, riga.groupId);
        setPendenti((prev) => prev.filter(
            (m) => !(m.tipo === 'rimozione'
                && chiaveConcessione(m.roleId, m.groupId) === chiave)));
    };

    /**
     * Scrive le modifiche preparate, <b>una alla volta e in ordine</b>.
     *
     * Non in parallelo: l'invariante «resta almeno un amministratore» valuta lo stato del
     * momento, e due chiamate concorrenti possono vederlo entrambe soddisfatto e lasciarlo
     * violato. In ordine, inoltre, chi sposta una persona fra due gruppi mette prima la
     * concessione nuova e toglie dopo la vecchia se le ha preparate in quell'ordine.
     *
     * Cio' che fallisce resta preparato: sparire in silenzio farebbe credere che sia stato
     * scritto.
     */
    const handleSalva = async () => {
        setSalvataggio(true);
        setGrantsError(null);
        const rifiutate = [];
        for (const modifica of pendenti) {
            try {
                if (modifica.tipo === 'aggiunta') {
                    // eslint-disable-next-line no-await-in-loop
                    await userAdminService.grant(sub, modifica.roleId, modifica.groupId);
                } else {
                    // eslint-disable-next-line no-await-in-loop
                    await userAdminService.revokeGrant(sub, modifica.roleId, modifica.groupId);
                }
            } catch (err) {
                rifiutate.push({
                    modifica,
                    messaggio: err?.response?.data?.message || 'operazione rifiutata',
                });
            }
        }
        setPendenti(rifiutate.map((r) => r.modifica));
        // Anche un salvataggio in parte rifiutato ha cambiato qualcosa: chi elenca gli utenti
        // deve saperlo, o alla chiusura mostrerebbe la persona com'era prima.
        if (rifiutate.length < pendenti.length) {
            if (onSaved) {
                onSaved();
            }
            // La persona puo' essere chi sta salvando: senza rilettura i suoi pulsanti
            // resterebbero quelli di prima fino al ricaricamento della pagina. Una GET, e
            // non si controlla chi sia: costa meno che sbagliarlo.
            if (refreshPermissions) {
                refreshPermissions();
            }
        }
        setSalvataggio(false);
        // Prima il ricaricamento e poi l'errore: `loadGrants` azzera l'errore appena parte,
        // e nell'ordine inverso il motivo del rifiuto spariva prima di essere letto.
        loadGrants();
        if (rifiutate.length > 0) {
            setGrantsError(rifiutate
                .map((r) => `${r.modifica.roleLabel}: ${r.messaggio}`)
                .join(' · '));
        }
    };

    const handleShowEffective = () => {
        setLoadingEffective(true);
        setError(null);
        userAdminService.getEffectivePermissions(sub).then(
            (data) => {
                setEffectivePermissions(data || []);
                setLoadingEffective(false);
            },
            (err) => {
                setLoadingEffective(false);
                setError(
                    err?.response?.data?.message ||
                        'Non è stato possibile caricare i permessi effettivi.',
                );
            },
        );
    };

    return (
        <Dialog open={Boolean(sub)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.light',
                            color: 'primary.main',
                            fontSize: '0.8rem',
                        }}
                    >
                        {iniziali(user?.displayName)}
                    </Avatar>
                    <Box>
                        {user ? user.displayName : 'Utente'}
                        {user && (
                            <Typography variant="body2" color="text.secondary">
                                {user.email}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={onClose} aria-label="chiudi" size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && <Typography variant="body2">Caricamento…</Typography>}

                {!loading && (
                    <>
                        {catalogError && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {catalogError}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}


                        {!effectivePermissions && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle1" gutterBottom>
                                    Ruoli, e in quali gruppi
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    {SPIEGAZIONE_CONCESSIONI}
                                </Typography>

                                {grantsError && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                        {grantsError}
                                    </Alert>
                                )}

                                <ConcessioniPerRuolo
                                    voci={concessioniPerRuolo}
                                    permessiPerRuolo={permessiPerRuolo}
                                    totalPermissions={totalPermissions}
                                    canUpdate={canUpdate}
                                    vuoto={righeConcessioni.length === 0 && !grantsError}
                                    onRevoke={handleRevoke}
                                    onRipristina={handleRipristina}
                                />

                                {canUpdate && (
                                    <FormConcessione
                                        catalog={catalog}
                                        groups={groups}
                                        chiaviAttive={chiaviAttive}
                                        onPrepara={handlePrepara}
                                        onErrore={setGrantsError}
                                    />
                                )}
                            </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            Code visibili{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                                (in sola lettura)
                            </Typography>
                        </Typography>
                        <List dense>
                            {(user?.queues || []).map((coda) => (
                                <ListItem key={coda}>
                                    <ListItemText primary={coda} />
                                </ListItem>
                            ))}
                            {(user?.queues || []).length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Nessuna coda visibile: i gruppi di questa persona non ne
                                    contengono.
                                </Typography>
                            )}
                        </List>
                        {/*
                          * Chi cerca «aggiungi una coda a questa persona» deve trovare una
                          * spiegazione, non un'assenza: le code non si amministrano per la
                          * singola persona. Dalla fase F3 la seconda leva non e' piu' «le
                          * code del ruolo» ma «le code del gruppo», ed e' un cambiamento
                          * che va detto qui — chi legge la frase vecchia andrebbe a cercare
                          * un tab Code sul ruolo che non governa piu' nulla.
                          */}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Le code arrivano dai gruppi: si cambiano da qui sopra, oppure sulla
                            pagina del gruppo — dove però valgono per tutti i suoi membri.
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {/*
                          * §10.4 riga 972 chiama questo "un collegamento *Vedi permessi
                          * effettivi*", non un pulsante che affianca la lista dei ruoli: un
                          * collegamento porta altrove. Percio' le due sezioni — Ruoli e
                          * Permessi effettivi — si alternano invece di stare assieme: non e'
                          * un dettaglio di layout, e' la lettura corretta della specifica.
                          */}
                        {!effectivePermissions && (
                            <Button
                                variant="outlined"
                                onClick={handleShowEffective}
                                disabled={loadingEffective}
                            >
                                Vedi permessi effettivi
                            </Button>
                        )}
                        {effectivePermissions && (
                            <>
                                <Button variant="text" onClick={() => setEffectivePermissions(null)}>
                                    Torna ai ruoli
                                </Button>
                                <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                                    Permessi effettivi
                                </Typography>
                                <List dense sx={{ mt: 1 }}>
                                    {effectivePermissions.map((perm) => (
                                        <ListItem key={perm.code}>
                                            <ListItemText
                                                primary={perm.code}
                                                secondaryTypographyProps={{ component: 'div' }}
                                                secondary={
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                        {(perm.fromRoles || []).map((roleName) => (
                                                            <Chip key={roleName} size="small" label={roleName} />
                                                        ))}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                    {effectivePermissions.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            Nessun permesso effettivo.
                                        </Typography>
                                    )}
                                </List>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            {!loading && !canUpdate && (
                <Box sx={{ px: 3, pt: 1 }}>
                    <Alert severity="info">
                        Modificare i ruoli richiede il permesso <code>users.update</code>, che
                        manca: il pannello è in sola lettura.
                    </Alert>
                </Box>
            )}
            <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                {/* La latenza (§15.1) va detta accanto al pulsante che scrive, ed e' qui che
                    ora si scrive. Era un riquadro azzurro a tutta larghezza sopra i pulsanti:
                    a quel posto sembrava un avviso sulla finestra intera, e non si allineava
                    con niente. Come nota accanto a «Salva» dice la stessa cosa nel momento in
                    cui serve — quando si sta per premerlo. */}
                <Typography variant="caption" color="text.secondary"
                            sx={{ pl: 1, maxWidth: 320, textAlign: 'left', flexShrink: 1 }}>
                    {pendenti.length > 0
                        ? 'Chiudendo senza salvare le modifiche preparate vanno perse. Dopo il salvataggio servono fino a 60 secondi perché abbiano effetto ovunque.'
                        : 'Dopo il salvataggio servono fino a 60 secondi perché la modifica abbia effetto ovunque.'}
                </Typography>
                {/* `flexShrink: 0` e `nowrap`: la didascalia accanto e' lunga, e senza questi
                    i pulsanti si stringono fino ad andare a capo dentro se stessi — «Annulla
                    modifiche» su due righe e «Salva (1)» spezzato dal suo conteggio. */}
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {canUpdate && pendenti.length > 0 && (
                        <Button onClick={() => setPendenti([])} disabled={salvataggio}>
                            Annulla
                        </Button>
                    )}
                    <Button onClick={onClose}>Chiudi</Button>
                    {canUpdate && (
                        <Button
                            variant="contained"
                            onClick={handleSalva}
                            disabled={pendenti.length === 0 || salvataggio}
                        >
                            {salvataggio
                                ? 'Salvataggio…'
                                : pendenti.length > 0
                                ? `Salva (${pendenti.length})`
                                : 'Salva'}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

UserRolesPanel.propTypes = {
    sub: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
};
